import * as React from 'react';
import style from './index.module.scss';

export type ToggleGroupItem<T> = {
    label: React.ReactNode;
    value: T;
    disabled?: boolean;
};

export type ToggleGroupProps<T> = {
    items: ReadonlyArray<ToggleGroupItem<T>>;
    value: T;
    onChange: (value: T) => void;
    disabled?: boolean;
    ariaLabel?: string;
    className?: string;
};

export const ToggleGroup = <T,>({
    items,
    value,
    onChange,
    disabled,
    ariaLabel,
    className,
}: ToggleGroupProps<T>) => {
    const itemRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

    const isItemDisabled = (item: ToggleGroupItem<T>) => Boolean(disabled || item.disabled);

    const focusItem = (index: number) => {
        itemRefs.current[index]?.focus();
    };

    const findFirstEnabledIndex = () => items.findIndex(item => !isItemDisabled(item));

    const findLastEnabledIndex = () => {
        for (let index = items.length - 1; index >= 0; index--) {
            const item = items[index];
            if (item && !isItemDisabled(item)) {
                return index;
            }
        }
        return -1;
    };

    const findNextEnabledIndex = (startIndex: number, direction: 1 | -1) => {
        const length = items.length;
        if (length === 0) {
            return -1;
        }
        for (let offset = 1; offset <= length; offset++) {
            const index = (startIndex + offset * direction + length) % length;
            const item = items[index];
            if (item && !isItemDisabled(item)) {
                return index;
            }
        }
        return -1;
    };

    const groupClasses = [style.segmented, className];
    if (disabled) {
        groupClasses.push(style.disabled);
    }

    return (
        <div className={groupClasses.filter(Boolean).join(' ')} role="radiogroup" aria-label={ariaLabel}>
            {items.map((item, index) => {
                const checked = Object.is(item.value, value);
                const itemDisabled = isItemDisabled(item);
                const itemClasses = [style.item];
                if (checked) {
                    itemClasses.push(style.active);
                }
                return (
                    <button
                        key={index}
                        type="button"
                        role="radio"
                        aria-checked={checked}
                        disabled={itemDisabled}
                        tabIndex={checked ? 0 : -1}
                        className={itemClasses.join(' ')}
                        onClick={() => onChange(item.value)}
                        onKeyDown={(event) => {
                            if (disabled) {
                                return;
                            }
                            switch (event.key) {
                                case 'ArrowRight':
                                case 'ArrowDown': {
                                    event.preventDefault();
                                    const nextIndex = findNextEnabledIndex(index, 1);
                                    if (nextIndex === -1) {
                                        return;
                                    }
                                    const nextItem = items[nextIndex];
                                    if (!nextItem) {
                                        return;
                                    }
                                    onChange(nextItem.value);
                                    focusItem(nextIndex);
                                    break;
                                }
                                case 'ArrowLeft':
                                case 'ArrowUp': {
                                    event.preventDefault();
                                    const nextIndex = findNextEnabledIndex(index, -1);
                                    if (nextIndex === -1) {
                                        return;
                                    }
                                    const nextItem = items[nextIndex];
                                    if (!nextItem) {
                                        return;
                                    }
                                    onChange(nextItem.value);
                                    focusItem(nextIndex);
                                    break;
                                }
                                case 'Home': {
                                    event.preventDefault();
                                    const nextIndex = findFirstEnabledIndex();
                                    if (nextIndex === -1) {
                                        return;
                                    }
                                    const nextItem = items[nextIndex];
                                    if (!nextItem) {
                                        return;
                                    }
                                    onChange(nextItem.value);
                                    focusItem(nextIndex);
                                    break;
                                }
                                case 'End': {
                                    event.preventDefault();
                                    const nextIndex = findLastEnabledIndex();
                                    if (nextIndex === -1) {
                                        return;
                                    }
                                    const nextItem = items[nextIndex];
                                    if (!nextItem) {
                                        return;
                                    }
                                    onChange(nextItem.value);
                                    focusItem(nextIndex);
                                    break;
                                }
                            }
                        }}
                        ref={(node) => {
                            itemRefs.current[index] = node;
                        }}
                    >
                        {item.label}
                    </button>
                );
            })}
        </div>
    );
};
